import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useShopData } from '@/hooks/useShopData';
import { useSiteData } from '@/hooks/useSiteData';

export function Shop() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();
  const { pageData } = useShopData();

  return (
    <>
      <Helmet>
        <title>Shop - {siteData.title}</title>
        <meta
          name="description"
          content="Browse our club clothing collection. Baseball hats, fleece jackets, polo shirts, sweatshirts, and more with club branding."
        />
        <meta name="keywords" content="club clothing, shop, merchandise, apparel, hats, jackets, shirts" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">{pageData.title}</h1>
            <p className="text-lg text-neutral-600 leading-relaxed mb-4">
              {pageData.description}
            </p>
            <p className="text-sm text-neutral-500 italic">
              {pageData.notice}
            </p>
          </div>

          <div className="text-center mb-8">
            <a
              href={pageData.orderForm.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              📄 {pageData.orderForm.title}
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pageData.products.map((product) => (
              <div key={product.id} className="card-professional group">
                <div className="aspect-square overflow-hidden rounded-lg mb-4 bg-neutral-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-display font-semibold text-primary-700 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">
                    {product.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-neutral-50 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-display font-semibold text-primary-700 mb-4">
              How to Order
            </h2>
            <div className="space-y-3 text-neutral-700 max-w-2xl mx-auto">
              <p className="flex items-center justify-center gap-2">
                <span className="bg-primary-100 text-primary-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold">1</span>
                Download and print the order form above
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="bg-primary-100 text-primary-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold">2</span>
                Fill out the form legibly with your selections
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="bg-primary-100 text-primary-700 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold">3</span>
                Enclose a check and put in the clubhouse foyer mailbox or mail to the club address
              </p>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}